/**
 * The katana touches, using the katana from the Ryoma logo (public/brand/katana.svg):
 *   - pageCut: when a page opens, the katana draws the gold rule under its heading in one quick,
 *     straight stroke (about a quarter of a second) and is gone.
 *   - celebrate: when a lead becomes a client, the katana crosses the screen and the enso splits.
 *   - loading: a thin gold line runs along the top while a page takes a moment.
 * All of them are skipped when motion is reduced (computer or Settings) or the katana effects are
 * off, and none carries information that is not also shown as text.
 */
import { isDark, katanaOn } from "./prefs.ts";

/** The logo's katana: 430 by 1880, upright, tip at the bottom. */
export const KATANA_W = 430;
export const KATANA_H = 1880;
/** Where the tip sits across the image's width (the blade curves slightly). */
export const KATANA_TIP_X = 0.37;
export const katanaSrc = (onDark = isDark()): string => (onDark ? "/brand/katana-on-dark.svg" : "/brand/katana.svg");

/** One quick stroke along the rule under the page heading, drawing it as it passes. */
export function pageCut(root: ParentNode = document): void {
  if (!katanaOn()) return;
  const head = root.querySelector<HTMLElement>(".page-head");
  if (!head) return;
  head.querySelector(".cut-fx")?.remove();
  const fx = document.createElement("span");
  fx.className = "cut-fx";
  fx.setAttribute("aria-hidden", "true");
  const line = document.createElement("i");
  line.className = "cut-line";
  const blade = document.createElement("img");
  blade.className = "cut-blade";
  blade.src = katanaSrc();
  blade.alt = "";
  fx.append(line, blade);
  head.append(fx);
  head.classList.add("cutting");
  setTimeout(() => { fx.remove(); head.classList.remove("cutting"); }, 900);
}

let barTimer: ReturnType<typeof setTimeout> | undefined;
let bar: HTMLElement | null = null;

/** Shows the running line if loading takes longer than a moment. */
export function loading(on: boolean): void {
  clearTimeout(barTimer);
  if (!on) {
    bar?.remove();
    bar = null;
    return;
  }
  barTimer = setTimeout(() => {
    if (bar) return;
    bar = document.createElement("div");
    bar.className = "blade-bar";
    bar.setAttribute("role", "progressbar");
    bar.setAttribute("aria-label", "Loading");
    bar.append(document.createElement("i"));
    document.body.append(bar);
  }, 150);
}

/**
 * The client-won moment: the katana crosses in one stroke, leaves a gold cut, and the enso
 * splits along it. The words are also announced by the toast that goes with it.
 */
export function celebrate(word = "Client won"): void {
  if (!katanaOn()) return;
  const wrap = document.createElement("div");
  wrap.className = "celebrate";
  wrap.setAttribute("aria-hidden", "true");
  const safe = word.replace(/[<>&"]/g, "");
  // Katana 240 long, laid along the cut (-20°), tip leading.
  const L = 240, W = (L * KATANA_W) / KATANA_H;
  wrap.innerHTML = `<svg viewBox="-260 -170 520 340">
    <defs><clipPath id="c-top"><path d="M-300,-300 L300,-300 L300,-109 L-300,109 Z"/></clipPath><clipPath id="c-bot"><path d="M-300,109 L300,-109 L300,300 L-300,300 Z"/></clipPath></defs>
    <g class="c-half-a" clip-path="url(#c-top)"><circle r="96" fill="none" stroke="#C9A45E" stroke-width="16" stroke-linecap="round" stroke-dasharray="560 80" opacity=".95"/></g>
    <g class="c-half-b" clip-path="url(#c-bot)"><circle r="96" fill="none" stroke="#A9823F" stroke-width="16" stroke-linecap="round" stroke-dasharray="560 80" opacity=".95"/></g>
    <line class="c-line" x1="-250" y1="91" x2="250" y2="-91"/>
    <g class="c-blade"><g transform="rotate(-20) rotate(-90)"><image href="${katanaSrc()}" x="${(-W * KATANA_TIP_X).toFixed(1)}" y="${-L}" width="${W.toFixed(1)}" height="${L}"/></g></g>
    <text class="c-word" x="0" y="150" text-anchor="middle">${safe}</text>
  </svg>`;
  document.body.append(wrap);
  setTimeout(() => wrap.remove(), 2000);
}
